import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  addTelegramBotConfig,
  getTelegramBotConfigs,
  deleteTelegramBotConfig,
} from '../../../reducers/telegram/telegram-action';
import {
  changePage,
  changelimit,
  resetTelegramPagination,
} from '../../../reducers/telegram/telegram-slice';
import styles from './telegramBot.module.css';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import CustomBreadcrumb from '../../../components/CustomBreadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import TableActionsContainer from '../../../components/CustomTable/TableActionsContainer';
import { CustomButton } from '../../../components/CustomButton';
import { Text } from '../../../components/Text';
import CustomPaginator from '../../../components/CustomPaginator';
import Loader from '../../../components/Loader';
import { Add_NewBotConfiguration_ToastSuccessMessage } from '../../../config/ToastMessage';
import { getDate } from '../../../utils/datemaker';
import { ReactComponent as Delete } from '../../../svg/delete.svg';
import SklbtConfirmation from '../../../components/common/SklbtConfirmation';

const TelegramBotConfigs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Add config modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // delete bot state
  const [deleteBotAlert, setDeleteBotAlert] = useState(false);
  const [deleteBotId, setDeleteBotId] = useState(null);

  // QR modal states
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedBotConfig, setSelectedBotConfig] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const qrCanvasRef = useRef(null);
  const toast = useRef(null);

  const { botConfigs, loading, page, limit, totalCount } = useSelector((state) => state.telegram);

  //   const limit = 10;

  useEffect(() => {
    loadBotConfigs();
  }, [page, limit]);

  const loadBotConfigs = async () => {
    try {
      const response = dispatch(getTelegramBotConfigs({ page, limit }));
    } catch (err) {
      console.log('err', err?.message);
      toast.current.show({
        severity: 'error',
        detail: 'Something went wrong!',
      });
    }
  };

  const handleRefresh = () => {
    // setPage(0);
    loadBotConfigs();
  };

  const handleUseBotConfig = (botConfig) => {
    navigate(`/telegram-chat?botConfigId=${botConfig.id}`);
  };

  const handleAddConfig = () => {
    setIsModalOpen(true);
    setBotToken('');
  };

  const handleSubmitBotConfig = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await dispatch(addTelegramBotConfig({ token: botToken }))
        .unwrap()
        .then((res) => {
          let Message_Success = Add_NewBotConfiguration_ToastSuccessMessage;
          toast?.current?.show({
            severity: 'success',
            detail: Message_Success,
          });
        });

      setIsModalOpen(false);
      setBotToken('');
      loadBotConfigs();
    } catch (err) {
      toast.current.show({
        severity: 'error',
        detail: err?.message || 'Something went wrong!',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowQR = async (config) => {
    try {
      const url = `https://t.me/${config.userName}`;
      setSelectedBotConfig(config);
      setQrCodeUrl(url);
      setQrModalOpen(true);

      setTimeout(async () => {
        if (qrCanvasRef.current) {
          await QRCode.toCanvas(qrCanvasRef.current, url, {
            width: 256,
            margin: 2,
          });
        }
      }, 100);
    } catch (err) {
      console.log('QR error:', err?.message);
      toast.current.show({
        severity: 'error',
        detail: 'Something went wrong!',
      });
    }
  };

  /* ---------- TABLE COLUMN TEMPLATES ---------- */

  const statusTemplate = (row) => (
    <span className={`${styles.statusBadge} ${row.isActive ? 'active' : 'inactive'}`}>
      {row.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const qrButtonTemplate = (row) => (
    <CustomButton
      icon={'pi pi-qrcode'}
      varient="text"
      type={'button'}
      onClick={() => handleShowQR(row)}
    />
  );
  const prepareDeletGroup = (rowData) => {
    setDeleteBotAlert(true);
    setDeleteBotId(rowData.id);
  };

  const handleDeteteGroup = () => {
    if (!deleteBotId) return;
    dispatch(deleteTelegramBotConfig({ botId: deleteBotId }))
      .unwrap()
      .then(() => {
        setDeleteBotId(null);
        setDeleteBotAlert(false);
      })
      .catch((err) => {});
  };

  const actionsTemplate = (row) => (
    <div className="flex">
      <CustomButton
        varient="icon-button"
        onClick={() => prepareDeletGroup(row)}
        icon={<Delete />}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <Loader visible={loading} />
      <Toast ref={toast} />
      <CustomBreadcrumb
        home={{ label: 'Market Place', url: '/marketplace' }}
        itemslist={[{ label: 'Telegram', url: '#' }]}
      />

      {deleteBotAlert && (
        <SklbtConfirmation
          visible={deleteBotAlert}
          handleConfirm={handleDeteteGroup}
          handleReject={() => setDeleteBotAlert(false)}
          message={`Are you sure, this will delete the bot?`}
        />
      )}

      <div className={styles.configsContent}>
        <div className={styles.configsHeader}>
          <h1>🤖 Telegram Bot Configurations</h1>
          <p>Manage your Telegram bot configurations and access bot chats</p>
        </div>

        <TableActionsContainer>
          <div className={styles.tableHeader}>
            <Text type={'heading'}> Bot Configurations </Text>
            <div className={styles.headerActions}>
              {totalCount === 0 && (
                <CustomButton icon={'pi pi-plus'} label="Add Config" onClick={handleAddConfig} />
              )}
              {totalCount > 0 && (
                <CustomButton icon="pi pi-refresh" label="Refresh" onClick={handleRefresh} />
              )}
            </div>
          </div>
        </TableActionsContainer>

        {/* PRIME REACT TABLE */}
        <DataTable
          value={botConfigs}
          loading={loading}
          className="skalebot-table"
          tableStyle={{ minWidth: '60rem' }}
          dataKey="id"
        >
          <Column field="id" header="ID" className="text-sm font-normal" />
          <Column
            header="Date"
            body={(row) => getDate(row.createdAt)}
            className="text-sm font-normal"
          />
          <Column field="botId" header="Bot ID" className="text-sm font-normal" />
          <Column
            field="userName"
            header="Username"
            body={(row) => '@' + row.userName}
            className="text-sm font-normal"
          />
          <Column header="Status" body={statusTemplate} className="text-sm font-normal" />

          <Column header="QR" body={qrButtonTemplate} className="text-sm font-normal" />
          <Column header="Actions" body={actionsTemplate} className="text-sm font-normal" />
        </DataTable>
      </div>

      {/* Add Bot Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Add Bot</h3>

            <form onSubmit={handleSubmitBotConfig}>
              <input
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder="Bot Token"
              />

              <div className={styles.modalActions}>
                <CustomButton
                  label="Cancel"
                  type="button"
                  varient="outline"
                  onClick={() => setIsModalOpen(false)}
                />
                <CustomButton
                  label={isSubmitting ? 'Adding...' : 'Add Bot'}
                  disabled={isSubmitting || !botToken}
                  varient="filled"
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrModalOpen && selectedBotConfig && (
        <div className={styles.modalOverlay} onClick={() => setQrModalOpen(false)}>
          <div className={styles.qrModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>QR for @{selectedBotConfig.userName}</h3>
              <button className={styles.modalClose} onClick={() => setQrModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className={styles.qrModalBody}>
              <div className={styles.qrCodeContainer}>
                <canvas ref={qrCanvasRef} />
              </div>

              <div className={styles.qrInfo}>
                <p>
                  <strong>Bot Username:</strong> @{selectedBotConfig.userName}
                </p>
                <p>
                  <strong>Telegram Link:</strong>
                </p>
                <div className={styles.telegramLink}>
                  <a href={qrCodeUrl} target="_blank" rel="noopener noreferrer">
                    {qrCodeUrl}
                  </a>
                </div>
                <p className={styles.qrInstructions}>
                  Scan this QR code with your phone's camera or Telegram app to open the bot chat.
                </p>
              </div>
              <div className={styles.qrModalActions}>
                <Button
                  label="Close"
                  onClick={() => setQrModalOpen(false)}
                  className={styles.closeQrButton}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <TableActionsContainer>
        <div className="flex  justify-content-end">
          <CustomPaginator
            page={page}
            limit={limit}
            changelimit={changelimit}
            totalRecords={totalCount}
            changePage={changePage}
          />
        </div>
      </TableActionsContainer>
    </div>
  );
};

export default TelegramBotConfigs;
